import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  FiShield, FiHeart, FiUsers, FiDollarSign, FiFileText, FiCheckCircle,
  FiChevronRight, FiLayers
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const brand = {
  bg: "bg-[#f5f8fc]",
  text: "text-[#0f2437]",
  btnPrimary:
    "bg-[#002b5c] text-white hover:bg-[#08336e] focus:ring-2 focus:ring-[#002b5c]/30",
  card: "bg-white rounded-2xl shadow-sm border border-slate-100",
  chip: "inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm font-medium bg-slate-100 text-slate-700",
};

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6 },
};

export default function HealthInsurance() {
  return (
    <>
      <Helmet>
        <title>Health Insurance | Your Choice Associates</title>
        <meta
          name="description"
          content="Compare health insurance options — Marketplace (ACA), Individual & Family, Short-Term, and dental/vision add-ons. Start with a licensed broker."
        />
      </Helmet>

      <Navbar />

      <main className={`${brand.bg} ${brand.text}`}>
        {/* Hero */}
        <section className="bg-[#072e57] text-white">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-16 md:py-20">
            <motion.div {...fade}>
              <span className="inline-block mb-3 text-xs tracking-widest uppercase opacity-80">
                Your Choice Associates
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
                Health Insurance — made simple and affordable
              </h1>
              <p className="mt-4 text-white/90 max-w-2xl">
                We’ll compare plans, find savings, and enroll you in coverage that fits your doctors, prescriptions, and budget.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#options" className={`px-5 py-3 rounded-xl bg-white/10 hover:bg-white/20`}>Explore options</a>
                {/* CTA → /start per instruction */}
                <a href="/start" className={`px-5 py-3 rounded-xl ${brand.btnPrimary}`}>Get help now</a>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <span className={brand.chip}><FiShield /> Licensed brokers</span>
                <span className={brand.chip}><FiDollarSign /> No extra fees</span>
                <span className={brand.chip}><FiCheckCircle /> Carrier-neutral</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Quick links */}
        <section id="options" className="max-w-6xl mx-auto px-6 md:px-8 py-12">
          <motion.div {...fade}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { href: "#individual", icon: <FiUsers />, label: "Individual & Family" },
                { href: "/marketplace", icon: <FiLayers />, label: "Marketplace (ACA)" },
                { href: "#shortterm", icon: <FiShield />, label: "Short-Term Medical" },
                { href: "#dental", icon: <FiHeart />, label: "Dental & Vision" },
              ].map((item) => (
                <a key={item.label} href={item.href} className={`${brand.card} p-4 group hover:shadow-md transition`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-slate-100">{item.icon}</div>
                    <div className="font-semibold">{item.label}</div>
                    <FiChevronRight className="ml-auto opacity-50 group-hover:translate-x-0.5 transition" />
                  </div>
                </a>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Plan types */}
        <section className="max-w-6xl mx-auto px-6 md:px-8 pb-6">
          <motion.div {...fade} className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold">Common plan types</h2>
            <p className="text-slate-600 mt-2">Balance cost, flexibility, and doctor access.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "HMO", desc: "Lowest cost; referrals; limited network." },
              { title: "PPO", desc: "Flexible choice; no referrals; higher premiums." },
              { title: "EPO", desc: "In-network only; no referrals." },
              { title: "POS", desc: "HMO-like with some out-of-network benefits." },
            ].map((c) => (
              <motion.div key={c.title} {...fade} className={`${brand.card} p-5`}>
                <div className="text-lg font-bold">{c.title}</div>
                <p className="text-slate-600 mt-2">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Individual & Family */}
        <section id="individual" className="max-w-6xl mx-auto px-6 md:px-8 py-10">
          <motion.div {...fade} className={`${brand.card} p-6 md:p-8`}>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-bold">Individual & Family Plans</h3>
                <p className="text-slate-600 mt-2">
                  Comprehensive coverage if you’re not insured through an employer. We’ll evaluate networks, deductibles, OOP maximums, and drug tiers so you know your real costs.
                </p>
                <ul className="mt-4 space-y-2 text-slate-700">
                  <li className="flex gap-2"><FiCheckCircle className="mt-1" /> Keep your doctors & meds</li>
                  <li className="flex gap-2"><FiCheckCircle className="mt-1" /> Transparent cost comparisons</li>
                  <li className="flex gap-2"><FiCheckCircle className="mt-1" /> Year-round support</li>
                </ul>
                <div className="mt-6 flex gap-3">
                  <a href="/start" className={`px-5 py-3 rounded-xl ${brand.btnPrimary}`}>Talk to a broker</a>
                  <a href="/start" className="px-5 py-3 rounded-xl bg-slate-100">Questionnaire</a>
                </div>
              </div>
              <div className="w-full lg:w-80">
                <div className="p-5 rounded-2xl bg-[#002b5c] text-white h-full">
                  <div className="font-semibold">What you’ll need</div>
                  <ul className="mt-3 text-white/90 space-y-2">
                    <li className="flex gap-2"><FiFileText className="mt-1" /> Doctors & prescriptions</li>
                    <li className="flex gap-2"><FiDollarSign className="mt-1" /> Budget & deductible comfort</li>
                    <li className="flex gap-2"><FiUsers className="mt-1" /> Household / ages</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Short-term */}
        <section id="shortterm" className="max-w-6xl mx-auto px-6 md:px-8 pb-10">
          <motion.div {...fade} className={`${brand.card} p-6 md:p-8`}>
            <h3 className="text-xl md:text-2xl font-bold">Short-Term Medical</h3>
            <p className="text-slate-600 mt-2">
              Temporary coverage (1–12 months) to bridge gaps. Lower premiums, limited benefits, and medical underwriting apply.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={brand.chip}><FiDollarSign /> Lower premiums</span>
              <span className={brand.chip}><FiShield /> Limited benefits</span>
            </div>
            <a href="/start" className={`inline-block mt-6 px-5 py-3 rounded-xl ${brand.btnPrimary}`}>
              Discuss short-term options
            </a>
          </motion.div>
        </section>

        {/* Dental & Vision */}
        <section id="dental" className="max-w-6xl mx-auto px-6 md:px-8 pb-10">
          <motion.div {...fade} className={`${brand.card} p-6 md:p-8`}>
            <h3 className="text-xl md:text-2xl font-bold">Dental & Vision add-ons</h3>
            <p className="text-slate-600 mt-2">Keep routine care affordable with plans for cleanings, fillings, frames, and lenses.</p>
            <div className="mt-4">
              <a href="/start" className={`px-5 py-3 rounded-xl ${brand.btnPrimary}`}>See plans</a>
            </div>
          </motion.div>
        </section>

        {/* CTA */}
        <section className="bg-white border-t">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-12">
            <motion.div {...fade} className="text-center">
              <h3 className="text-2xl md:text-3xl font-bold">Ready to compare plans?</h3>
              <p className="text-slate-600 mt-2">It’s free to work with us — we’re paid by carriers, not by you.</p>
              <div className="mt-6 flex justify-center gap-3">
                <a href="/start" className={`px-5 py-3 rounded-xl ${brand.btnPrimary}`}>Start</a>
                <a href="/marketplace" className="px-5 py-3 rounded-xl bg-slate-100">Go to Marketplace</a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
